// React Imports
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Backdrop,
  Popover,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';

// ** Icon Imports
import Icon from "../../components/icon";
import AddIcon from "@mui/icons-material/Add";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Logic Imports
import { useIndexLogic } from "./logic/index-logic";
import { useInvoicePermission } from "./permissions/InvoicePermissions";
import ImportExportOptions from "components/ImportExportOptions";

const dropdownItemsStyle = {
  px: 6, 
  py: 2, 
  '&:hover': { 
    backgroundColor: 'action.hover', cursor: 'pointer' 
  }
}

const OptionsButton = ({}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <IconButton onClick={handleClick}>
        <Icon icon='tabler:dots-vertical' />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={dropdownItemsStyle}>
              Send to Customer
            </Typography>
            {/* <Typography variant="h6" sx={dropdownItemsStyle}>
              Log Payment
            </Typography> */}
            {/* <Typography variant="h6" sx={dropdownItemsStyle}>
              Refund
            </Typography> */}
            <Typography variant="h6" sx={dropdownItemsStyle}>
              Resend Invoice
            </Typography>
            {/* <Typography variant="h6" sx={dropdownItemsStyle}>
              Mark as Complete
            </Typography> */}
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const Invoice = () => {
  const navigate = useNavigate();
  
  const { IndexProps } = useIndexLogic();
  const {
    columns,
    contentHeightFixed,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
  } = IndexProps;

  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSavePermission,
    hasInvoiceGetPermission,
    hasInvoiceDeletePermission,
  } = InvoicePermissionProps;

  return (
    <UserLayout contentHeightFixed={contentHeightFixed} activeLink={"Invoices"}>
      <Card>
        <CardHeader
          title={
            <Box
              sx={{
                mb: { xs: 3, sm: 0 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: 4,
                  py: 2,
                  maxWidth: "max-content",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography variant="body2" sx={{ pointerEvents: "none" }}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <CustomTextField
                value={searchText}
                placeholder="Search"
                disabled={!hasInvoiceGetPermission}
                {...(hasInvoiceGetPermission && {
                  onChange: (e) => handleSearch(e.target.value),
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <Icon icon="tabler:x" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="main"
                endIcon={<AddIcon />}
                disabled={!hasInvoiceSavePermission}
                {...(hasInvoiceSavePermission && {
                  onClick: () => navigate("/invoices/create"),
                })}
              >
                New Invoice
              </Button>
              {/* {hasInvoiceSavePermission && <OptionsButton />} */}
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={filteredRows}
                module="Invoices"
              />                
            </Box>
          }
          sx={{
            py: 4,
            flexDirection: ["column", "row"],
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: ["flex-start", "center"],
          }}
        />
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 4,
              alignItems: "center",
              mx: 6,
              mt: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: 4,
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                }}
              >
                {selectedRows.length}{" "}
                {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
            <CustomTextField
              select
              defaultValue="0"
              disabled={!hasInvoiceSavePermission}
              {...(hasInvoiceDeletePermission && {
                onChange: () => console.log(""), // Note: Whoever integrates it replace the handler with console.log
              })}
            >
              <MenuItem value="0" disabled>
                Status
              </MenuItem>
              <MenuItem value="active">Planning</MenuItem>
              <MenuItem value="active">Scheduled</MenuItem>
              <MenuItem value="active">Execution</MenuItem>
              <MenuItem value="active">Completed</MenuItem>
              <MenuItem value="active">Invoiced</MenuItem>
            </CustomTextField>
            <Button
              variant="contained"
              color="main"
              disabled={!hasInvoiceSavePermission}
              {...(hasInvoiceDeletePermission && {
                onClick: () => console.log(""), // Note: Whoever integrates it replace the handler with console.log
              })}
            >
              Apply Changes
            </Button>
            <Divider orientation="vertical" flexItem />
            <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: 1,
                display: "flex",
                gap: 2,
                px: 2,
              }}
            >
              <Tooltip title="Delete Selected">
                <IconButton
                  color="error"
                  disabled={!hasInvoiceDeletePermission}
                  {...(hasInvoiceDeletePermission && {
                    onClick: () => console.log("Delete Invoice"), // Note: Whoever integrates it replace the handler with console.log
                  })}
                >
                  <Icon icon="tabler:trash" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ height: "75vh" }}>
          <DataGridPro
            // pagination
            rows={filteredRows}
            rowHeight={62}
            columns={columns}
            checkboxSelection
            // pageSizeOptions={[
            //   10,
            //   50,
            //   { value: filteredRows.length, label: "Show All" },
            // ]}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={setPaginationModel}
            slots={{
              footer: () => null
            }}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelectionModel) => {
              handleGridSelection(newSelectionModel);
              setRowSelectionModel(newSelectionModel);
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
            }}
          />
        </Box>
      </Card>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Invoice;
